/**
 * @file apps/web/src/pages/BuildPage.tsx
 * @description Build phase — checklist of approved design items grouped by
 * type, each trackable as Not Started / In Progress / Built / Not
 * Applicable. Progress summary at top; reason field for Not Applicable.
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../lib/auth.js';
import { designItemsApi } from '../lib/api.js';
import { notifyProjectOverviewChanged } from '../lib/projectOverviewEvents.js';
import { Badge, type BadgeVariant } from '../components/Badge.js';
import type { DesignItem } from '../components/DesignItemPanel.js';

type UpdateFn = (itemId: string, updates: { buildStatus?: string; buildNotes?: string }) => Promise<void>;

const TYPE_LABELS: Record<string, string> = {
  suiteflow: 'SuiteFlow', suitescript: 'SuiteScript', custom_field: 'Custom Field',
  suiteapp: 'SuiteApp', bundle: 'Bundle', configuration: 'Configuration', other: 'Other',
};
const TYPE_ORDER = Object.keys(TYPE_LABELS);
const BUILD_STATUS_OPTIONS = ['not_started', 'in_progress', 'built', 'not_applicable'];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// Item row / type group
// ---------------------------------------------------------------------------

function BuildItemRow({ item, onUpdate }: { item: DesignItem; onUpdate: UpdateFn }) {
  const [notes, setNotes] = useState(item.build_notes ?? '');
  const [saving, setSaving] = useState(false);

  const setStatus = async (buildStatus: string) => {
    setSaving(true);
    try {
      await onUpdate(item.id, { buildStatus });
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      await onUpdate(item.id, { buildNotes: notes });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="px-3 py-3 rounded-lg hover:bg-gray/20 transition-colors space-y-2">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-ink truncate">{item.title}</p>
          {item.module_tag && (
            <p className="text-[10px] font-mono text-muted/70 mt-0.5">
              {[item.module_tag, item.process_area].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <Badge variant={item.type as BadgeVariant} size="xs" className="shrink-0" />
      </div>

      <div className="flex flex-wrap gap-1.5">
        {BUILD_STATUS_OPTIONS.map((value) => (
          <button
            key={value}
            onClick={() => void setStatus(value)}
            disabled={saving}
            className={[
              'text-xs font-mono px-2.5 py-1.5 rounded-full border transition-colors',
              item.build_status === value
                ? 'border-blue bg-sky text-blue'
                : 'border-[rgba(8,17,31,0.12)] text-muted hover:text-ink',
            ].join(' ')}
          >
            <Badge variant={value as BadgeVariant} size="xs" className={item.build_status === value ? '' : 'opacity-60'} />
          </button>
        ))}
      </div>

      {item.build_status === 'built' && (
        <p className="text-xs text-success">
          Built{item.built_by_name ? ` by ${item.built_by_name}` : ''}{item.built_at ? ` on ${formatDate(item.built_at)}` : ''}
        </p>
      )}

      {item.build_status === 'not_applicable' && (
        <input
          type="text"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={() => void saveNotes()}
          placeholder="Reason this item is not applicable…"
          className="input text-xs py-1.5 w-full"
        />
      )}
    </div>
  );
}

function TypeGroup({ type, items, onUpdate }: { type: string; items: DesignItem[]; onUpdate: UpdateFn }) {
  const built = items.filter((i) => i.build_status === 'built' || i.build_status === 'not_applicable').length;
  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between gap-3 p-3 border-b border-[rgba(8,17,31,0.06)]">
        <span className="text-sm font-medium text-ink">{TYPE_LABELS[type] ?? type}</span>
        <span className="text-[11px] font-mono text-muted">{built} / {items.length} built</span>
      </div>
      <div className="p-1.5 space-y-0.5">
        {items.map((item) => <BuildItemRow key={item.id} item={item} onUpdate={onUpdate} />)}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// BuildPage
// ---------------------------------------------------------------------------

export function BuildPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { devHeaders } = useAuth();

  const [items, setItems]     = useState<DesignItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const grouped = await designItemsApi.list(projectId, devHeaders) as Record<string, DesignItem[]>;
      setItems(Object.values(grouped).flat().filter((i) => i.status === 'approved'));
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load design items');
    } finally {
      setLoading(false);
    }
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);

  const handleUpdate: UpdateFn = async (itemId, updates) => {
    if (!projectId) return;
    await designItemsApi.update(projectId, itemId, updates, devHeaders);
    await load();
    notifyProjectOverviewChanged(projectId); // built/not-applicable counts feed the Build gate
  };

  if (!projectId) return null;

  const builtCount = items.filter((i) => i.build_status === 'built').length;
  const notApplicableCount = items.filter((i) => i.build_status === 'not_applicable').length;

  const byType: Record<string, DesignItem[]> = {};
  for (const item of items) {
    byType[item.type] ??= [];
    byType[item.type]!.push(item);
  }
  const visibleTypes = TYPE_ORDER.filter((t) => byType[t]?.length);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="font-display text-xl font-semibold text-ink">Build</h2>
        <p className="text-xs font-mono text-muted mt-0.5">
          Track construction of every approved design item.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-danger/8 rounded-lg text-danger text-sm">
          <AlertCircle size={14} className="shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="card h-64 animate-pulse bg-gray/60" />
      ) : items.length === 0 ? (
        <div className="empty-state py-16">
          <p className="text-sm text-muted">No approved design items yet.</p>
          <p className="text-xs text-muted/70 mt-1">
            Approve items on the Design page first.
          </p>
          <Link to={`/projects/${projectId}/design`} className="btn-primary mt-4 inline-flex">
            Go to Design
          </Link>
        </div>
      ) : (
        <>
          <div className="card p-4">
            <p className="text-2xl font-display font-semibold text-ink">
              {builtCount} of {items.length} items built
              {notApplicableCount > 0 && (
                <span className="text-sm font-mono text-muted ml-2">(+{notApplicableCount} not applicable)</span>
              )}
            </p>
          </div>
          <div className="space-y-2">
            {visibleTypes.map((type) => (
              <TypeGroup key={type} type={type} items={byType[type]!} onUpdate={handleUpdate} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
